import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { Alert, Button, Card, Col, Form, Input, message, Modal, Row, Space, Typography } from "antd";
import ChatPanel from "../components/ChatPanel";
import IntakeChecklist, {
  type ChecklistEntry,
  type ChecklistTopic,
} from "../components/intake/IntakeChecklist";
import {
  IntakeNameGenerationError,
  postAgencyIntake,
  streamAgencyIntake,
  submitAgencyIntake,
} from "../lib/api";
import agencyIntakeSchema from "../data/agency_intake_schema.json";
import agencyIntakeSample from "../data/agency_intake_sample.json";
import { useAuth } from "../lib/auth";
import { APP_BRAND_NAME } from "../lib/brand";

const sessionId = crypto.randomUUID();

const DIT_PRIMARY = "#092940";
const DIT_LOGO_URL =
  "https://files.nc.gov/dit/dit-logo_0.png?VersionId=hXzat98jbejrN24AhcEkTkX4mhZaJBAO";
const DIT_LOGO_ALT = "NC Department of Information Technology logo";

export default function AgencyIntake() {
  const { user } = useAuth();
  const [draftAnswers, setDraftAnswers] = useState<
    Array<{ questionId: string; stepTitle: string; question: string; answer: string }>
  >([]);
  const [intakeId, setIntakeId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [seedToken, setSeedToken] = useState(0);
  const [seedMessages, setSeedMessages] = useState<
    Array<{ role: "assistant" | "user"; text: string }>
  >([]);
  const [submitterInfo, setSubmitterInfo] = useState<{
    name: string;
    email: string;
    agency: string;
  } | null>(null);
  const [submitterForm] = Form.useForm();
  const isLocalhost = useMemo(
    () => ["localhost", "127.0.0.1"].includes(window.location.hostname),
    []
  );

  const checklistTopics: ChecklistTopic[] = useMemo(
    () =>
      agencyIntakeSchema
        .filter((item) => !item.internal)
        .map((item) => ({
          id: item.id,
          stepTitle: item.stepTitle,
          question: item.question,
          description: item.description,
        })),
    []
  );
  const [checklistState, setChecklistState] = useState<Record<string, ChecklistEntry>>(() =>
    Object.fromEntries(checklistTopics.map((t) => [t.id, { status: "pending" as const }]))
  );
  const [gapHint, setGapHint] = useState<string | null>(null);
  const [gapTopicIds, setGapTopicIds] = useState<string[]>([]);

  const openingPrompt = submitterInfo
    ? `Hi ${submitterInfo.name || "there"} from ${submitterInfo.agency || "your agency"}! ` +
      `I'm ${APP_BRAND_NAME}. Tell me what you have about your AI project — paste an existing brief, ` +
      `upload a PDF/Word doc/screenshot, or just describe it. The checklist on the right tracks the ` +
      `7 topics OAIP needs; I'll fill it in as you share.`
    : undefined;

  const buildSeedMessages = () => {
    const replacements: Record<string, string> = {
      "{AGENCY}": "NC Department of Public Safety",
      "{COUNTY}": "Wake County",
      "{DOC_ID}": `DOC-${Math.floor(1000 + Math.random() * 9000)}`,
    };

    return agencyIntakeSample.map((item) => {
      let text = item.text;
      Object.entries(replacements).forEach(([token, value]) => {
        text = text.replaceAll(token, value);
      });
      return { role: item.role as "assistant" | "user", text };
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", fontFamily: "'Source Sans Pro', system-ui, sans-serif" }}>
      <div
        style={{
          background: DIT_PRIMARY,
          color: "#FFFFFF",
          padding: "20px 24px",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <img
          src={DIT_LOGO_URL}
          alt={DIT_LOGO_ALT}
          style={{ height: 56, width: "auto", background: "#FFFFFF", padding: 6, borderRadius: 4 }}
        />
        <div>
          <Typography.Title level={2} style={{ margin: 0, color: "#FFFFFF" }}>
            Agency Intake
          </Typography.Title>
          <Typography.Text style={{ color: "#EEF7FF" }}>
            Share what you have on your AI project; {APP_BRAND_NAME} extracts the 7 OAIP intake topics as you go.
          </Typography.Text>
        </div>
      </div>

      {draftAnswers.length === 0 && submitterInfo ? (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {gapHint ? (
                <Alert
                  type="info"
                  showIcon
                  message={
                    gapTopicIds.length > 0
                      ? `Still need: ${gapTopicIds
                          .map(
                            (id) =>
                              checklistTopics.find((t) => t.id === id)?.stepTitle || id
                          )
                          .join(", ")}`
                      : "More info needed"
                  }
                  description={gapHint}
                />
              ) : null}
              <ChatPanel
                title="Start an AI project intake"
                description="Share what you have — paste a brief, upload a PDF/Word doc/image, or describe the project. The checklist on the right tracks the 7 topics OAIP needs."
                placeholder="Paste your project description, or attach a doc and ask me to extract from it..."
                seedMessages={seedMessages}
                seedToken={seedToken}
                openingPrompt={openingPrompt}
                onSend={async (message, handlers, _history, files) => {
                  const attachments = (files || [])
                    .filter((f) => f.content)
                    .map((f) => ({ mimeType: f.type, data: f.content, name: f.file.name }));
                  const { meta } = await streamAgencyIntake(
                    {
                      message,
                      sessionId,
                      submitterInfo,
                      context: {
                        userInfo: submitterInfo,
                      },
                      ...(attachments.length > 0 ? { attachments } : {}),
                    },
                    {
                      onDelta: handlers.onChunk,
                      onThought: handlers.onThought,
                      onMeta: (meta: any) => {
                        if (meta && typeof meta === "object") {
                          if (typeof meta.intakeId === "string") {
                            setIntakeId(meta.intakeId);
                          }
                          if (meta.checklistState && typeof meta.checklistState === "object") {
                            setChecklistState(meta.checklistState);
                          }
                          if (typeof meta.gapHint === "string") {
                            setGapHint(meta.gapHint);
                          } else if (meta.checklistState) {
                            setGapHint(null);
                          }
                          if (Array.isArray(meta.gapTopicIds)) {
                            setGapTopicIds(meta.gapTopicIds);
                          } else if (meta.checklistState) {
                            setGapTopicIds([]);
                          }
                        }
                        handlers.onMeta(meta);
                      },
                    }
                  );
                  if (Array.isArray(meta?.draftAnswers)) {
                    setDraftAnswers(meta.draftAnswers);
                  }
                  return undefined;
                }}
              />
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <IntakeChecklist topics={checklistTopics} state={checklistState} />
          </Col>
        </Row>
      ) : null}

      {isLocalhost && draftAnswers.length === 0 && (
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              const seeded = buildSeedMessages();
              void postAgencyIntake({
                sessionId,
                seedHistory: seeded,
                submitterInfo,
                context: {
                  userInfo: submitterInfo,
                },
              });
              setSeedMessages(seeded);
              setSeedToken((prev) => prev + 1);
            }}
          >
            Insert sample intake
          </Button>
        </Space>
      )}

      {draftAnswers.length > 0 && (
        <Card>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div>
              <Typography.Title level={4} style={{ marginBottom: 0 }}>
                Draft Answers
              </Typography.Title>
              <Typography.Text type="secondary">
                {submitted
                  ? "Your intake has been submitted to OAIP."
                  : "Edit anything that needs adjusting, then click Submit Intake when you're ready."}
              </Typography.Text>
            </div>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {agencyIntakeSchema.map((item) => {
                const draft = draftAnswers.find((answer) => answer.questionId === item.id);
                return (
                  <Card key={item.id} size="small">
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                      <Typography.Text type="secondary">{item.stepTitle}</Typography.Text>
                      <Typography.Text strong>{item.question}</Typography.Text>
                      <Input.TextArea
                        value={draft?.answer || ""}
                        disabled={submitted || submitting}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                          const nextValue = event.target.value;
                          setDraftAnswers((prev) =>
                            prev.map((entry) =>
                              entry.questionId === item.id ? { ...entry, answer: nextValue } : entry
                            )
                          );
                        }}
                        placeholder="Draft answer will appear here..."
                        autoSize={{ minRows: 4 }}
                      />
                    </Space>
                  </Card>
                );
              })}
            </Space>
            {submitError && (
              <Alert
                type="error"
                showIcon
                closable
                message={submitError}
                onClose={() => setSubmitError(null)}
              />
            )}
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                type="primary"
                size="large"
                loading={submitting}
                disabled={submitted || !intakeId || draftAnswers.length === 0}
                onClick={async () => {
                  if (!intakeId) return;
                  setSubmitting(true);
                  setSubmitError(null);
                  try {
                    await submitAgencyIntake({
                      intakeId,
                      draftAnswers: draftAnswers.map(({ questionId, answer }) => ({
                        questionId,
                        answer,
                      })),
                    });
                    setSubmitted(true);
                    void message.success("Intake submitted — OAIP will be in touch.");
                  } catch (err) {
                    if (err instanceof IntakeNameGenerationError) {
                      setSubmitError(
                        "We couldn't auto-name this intake. Please click Submit again — if it keeps failing, contact OAIP."
                      );
                    } else {
                      setSubmitError(
                        err instanceof Error
                          ? err.message
                          : "Couldn't submit the intake. Please try again."
                      );
                    }
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitted ? "Submitted ✓" : submitting ? "Submitting…" : "Submit Intake"}
              </Button>
            </Space>
          </Space>
        </Card>
      )}

      <Modal
        title="Before you begin"
        open={!submitterInfo}
        closable={false}
        maskClosable={false}
        okText="Continue"
        onOk={() => {
          submitterForm
            .validateFields()
            .then((values) => {
              setSubmitterInfo({
                name: values.name.trim(),
                email: values.email.trim(),
                agency: values.agency.trim(),
              });
            })
            .catch(() => undefined);
        }}
      >
        <Form
          form={submitterForm}
          layout="vertical"
          initialValues={{
            name: user?.displayName || "",
            email: user?.email || "",
            agency: "",
          }}
        >
          <Form.Item
            label="Your name"
            name="name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input placeholder="e.g., Jane Doe" />
          </Form.Item>
          <Form.Item
            label="Agency"
            name="agency"
            rules={[{ required: true, message: "Please enter your agency" }]}
          >
            <Input placeholder="e.g., NC Department of Public Safety" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Enter a valid email address" },
            ]}
          >
            <Input placeholder="name@agency.gov" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
